import {
  motion,
} from "framer-motion";

import {
  useTranslation,
} from "react-i18next";

/*
  ============================================================
  PHOTOGRAPHY PREMIUM
  EDITORIAL INTRO
  ============================================================

  Etusivun ensimmäinen sisältöosio heron jälkeen.

  Tarkoitus:
  - rauhoittaa hero-osuuden jälkeen
  - tuoda sivulle valokuvakirjamainen rytmi
  - esitellä kuvaajan ajatusmaailmaa
  - toimia sekä Ivory- että Noir-teemassa
*/

export default function EditorialIntro({
  theme = "ivory",
}) {
  const {
    t,
  } = useTranslation("common");

  const isNoir =
    theme === "noir";

  return (
    <section
      className={`
        relative
        overflow-hidden
        border-t
        border-border

        ${
          isNoir
            ? "bg-canvas"
            : "bg-surface"
        }
      `}
    >
      <div
        className="
          mx-auto
          w-full
          max-w-editorial
          px-6
          py-24

          sm:px-10
          sm:py-32

          lg:px-16
          lg:py-40
        "
      >
        {/* ====================================================
            TOP META
            ==================================================== */}

        <motion.div
          className="
            mb-16
            flex
            items-center
            justify-between
            gap-6
            border-b
            border-border
            pb-5

            sm:mb-24
          "
          initial={{
            opacity: 0,
            y: 16,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
            amount: 0.5,
          }}
          transition={{
            duration: 0.7,
            ease: [
              0.22,
              1,
              0.36,
              1,
            ],
          }}
        >
          <p
            className="
              m-0
              text-[9px]
              font-medium
              uppercase
              tracking-[0.28em]
              text-muted

              sm:text-[10px]
            "
          >
            01 / {t(
              "editorialIntro.eyebrow"
            )}
          </p>

          <span
            className="
              h-px
              w-12
              bg-border

              sm:w-20
            "
            aria-hidden="true"
          />
        </motion.div>

        {/* ====================================================
            EDITORIAL GRID
            ==================================================== */}

        <div
          className="
            grid
            gap-14

            lg:grid-cols-[minmax(0,1fr)_minmax(260px,0.32fr)]
            lg:items-end
            lg:gap-20
          "
        >
          {/* ==================================================
              MAIN STATEMENT
              ================================================== */}

          <motion.div
            initial={{
              opacity: 0,
              y: 32,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.35,
            }}
            transition={{
              duration: 0.95,
              ease: [
                0.22,
                1,
                0.36,
                1,
              ],
            }}
          >
            <p
              className="
                m-0
                max-w-[1050px]
                font-display
                text-[clamp(2.7rem,7vw,7rem)]
                font-normal
                leading-[0.94]
                tracking-[-0.045em]
                text-text
              "
            >
              {t(
                "editorialIntro.title"
              )}
            </p>
          </motion.div>

          {/* ==================================================
              SUPPORTING COPY
              ================================================== */}

          <motion.div
            className="
              max-w-md

              lg:pb-2
            "
            initial={{
              opacity: 0,
              y: 24,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.45,
            }}
            transition={{
              duration: 0.85,
              delay: 0.12,
              ease: [
                0.22,
                1,
                0.36,
                1,
              ],
            }}
          >
            <div
              className="
                mb-7
                h-px
                w-10
                bg-text
              "
              aria-hidden="true"
            />

            <p
              className="
                m-0
                text-sm
                leading-7
                text-muted

                sm:text-base
                sm:leading-8
              "
            >
              {t(
                "editorialIntro.description"
              )}
            </p>
          </motion.div>
        </div>

        {/* ====================================================
            BOTTOM DETAIL
            ==================================================== */}

        <motion.div
          className="
            mt-20
            flex
            items-end
            justify-between
            gap-8

            sm:mt-28
            lg:mt-36
          "
          initial={{
            opacity: 0,
          }}
          whileInView={{
            opacity: 1,
          }}
          viewport={{
            once: true,
            amount: 0.6,
          }}
          transition={{
            duration: 1,
            delay: 0.15,
          }}
        >
          <p
            className="
              m-0
              max-w-xs
              text-[9px]
              uppercase
              leading-5
              tracking-[0.2em]
              text-muted

              sm:text-[10px]
            "
          >
            {t(
              "editorialIntro.footer"
            )}
          </p>

          <span
            className="
              font-display
              text-3xl
              italic
              text-text/25

              sm:text-4xl
            "
            aria-hidden="true"
          >
            01
          </span>
        </motion.div>
      </div>
    </section>
  );
}
